import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import aviso, { textoDoErro } from '../lib/aviso';
import { LuArrowLeft, LuPencil, LuCalendar, LuPlus, LuTriangleAlert, LuUser } from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import Select from '../components/Select';
import InfoTitulo from '../components/InfoTitulo';
import RelatorioResultado from '../components/RelatorioResultado';
import PainelGrade, { proximaVaga, acomodar } from '../components/PainelGrade';
import BlocoEditor, { blocoNovo } from '../components/BlocoEditor';
import RelatorioPersonalizadoService, {
    consultaDoBloco, carregarValores,
    type BlocoPainel, type Catalogo, type FiltroEspecificacao, type PainelRelatorio,
    type RelatorioSalvo, type ResultadoRelatorio, type ValorDimensao,
} from '../services/RelatorioPersonalizadoService';
import { exportarResultadoCsv } from '../services/exportarCsv';
import './Relatorios.css';

/* O painel: a tela única de Relatórios desde a reorganização de 18/09.

   Antes eram duas — um visualizador de UM resultado e um construtor de cinco
   passos. As duas partiam da mesma ideia errada, a de que um relatório é uma
   pergunta com um formato. Um relatório é um conjunto de perguntas arrumadas
   numa folha, e é isso que esta tela é: blocos numa grade, um período no topo
   valendo para todos, e o editor de cada bloco em painel lateral, com o
   resultado mudando atrás enquanto se escolhe.

   A mesma tela serve três rotas:
     /relatorios/:id           leitura
     /relatorios/:id/editar    edição do que já existe
     /relatorios/novo/:modelo  edição de um painel que ainda não foi salvo

   A terceira é a que faz a biblioteca de modelos funcionar sem sujeira: o
   modelo é carregado, entra em edição e SÓ vira registro quando a pessoa
   salva. Quem desistir não deixa relatório órfão na galeria.                 */

interface EstadoBloco {
    resultado?: ResultadoRelatorio;
    erro?: string;
    carregando: boolean;
}

/** Só para a frase do editor — o período em palavras, como ele aparece no topo. */
const nomePreset = (catalogo: Catalogo | null, id: string) =>
    catalogo?.presets.find(p => p.id === id)?.nome ?? id;

export default function RelatorioPainelPage() {
    const { id, modelo } = useParams<{ id: string; modelo: string }>();
    const navegar = useNavigate();

    const rota = useLocation();

    const criando = modelo !== undefined;
    const rotaEdicao = rota.pathname.endsWith('/editar');

    const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
    const [relatorio, setRelatorio] = useState<RelatorioSalvo | null>(null);
    const [painel, setPainel] = useState<PainelRelatorio | null>(null);
    const [nome, setNome] = useState('');
    const [visibilidade, setVisibilidade] = useState<'pessoal' | 'empresa'>('pessoal');

    const [editando, setEditando] = useState(criando || rotaEdicao);
    const [selecionado, setSelecionado] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);

    /** Preset escolhido na leitura: é lente, não edição — não altera o salvo. */
    const [lente, setLente] = useState<string | null>(null);

    /* O filtro do topo (hoje só responsável) é lente pelo mesmo motivo que o período:
       o relatório pronto não se edita, e quem está olhando precisa recortar a tela
       inteira de uma vez. Vazio = todos. */
    const [filtroTopo, setFiltroTopo] = useState<Record<string, string>>({});
    const [valoresTopo, setValoresTopo] = useState<Record<string, ValorDimensao[]>>({});

    /** O último estado GRAVADO, para o Cancelar ter a que voltar. */
    const salvo = useRef<{ painel: PainelRelatorio; nome: string; visibilidade: 'pessoal' | 'empresa' } | null>(null);

    const [estados, setEstados] = useState<Record<string, EstadoBloco>>({});
    const assinaturas = useRef<Record<string, string>>({});
    const montado = useRef(true);
    useEffect(() => () => { montado.current = false; }, []);

    /* ─── carga ──────────────────────────────────────────────────── */

    useEffect(() => {
        let vivo = true;
        (async () => {
            setCarregando(true);
            setErro(null);
            try {
                const cat = await RelatorioPersonalizadoService.getCatalogo();
                if (!vivo) return;
                setCatalogo(cat);

                if (criando && modelo === 'branco') {
                    setPainel({
                        versao: 2,
                        periodo: { data: cat.fontes[0]?.datas[0]?.id ?? 'criado_em', preset: 'ultimos_30_dias' },
                        blocos: [blocoNovo(cat, 'b1', 0, 0)],
                    });
                    setNome('');
                    setSelecionado('b1');
                    return;
                }

                const alvo = criando ? Number(modelo) : Number(id);
                const r = await RelatorioPersonalizadoService.buscar(alvo);
                if (!vivo) return;

                setRelatorio(criando ? null : r);
                setPainel(r.painel);
                setNome(r.nome);
                setVisibilidade(criando ? 'pessoal' : r.visibilidade);
                setLente(r.painel?.periodo.preset ?? null);
                if (criando) setSelecionado(r.painel?.blocos[0]?.id ?? null);
                if (!criando && r.painel) {
                    salvo.current = { painel: r.painel, nome: r.nome, visibilidade: r.visibilidade };
                }
            } catch (e) {
                if (vivo) setErro(e instanceof Error ? e.message : 'Não foi possível abrir o relatório.');
            } finally {
                if (vivo) setCarregando(false);
            }
        })();
        return () => { vivo = false; };
    }, [id, modelo, criando]);

    const filtrosDeclarados = useMemo(() => painel?.filtrosDoPainel ?? [], [painel]);

    useEffect(() => {
        filtrosDeclarados.forEach(dimensao => {
            if (valoresTopo[dimensao]) return;
            void carregarValores(dimensao).then(valores => {
                if (montado.current && valores) setValoresTopo(v => ({ ...v, [dimensao]: valores }));
            });
        });
    }, [filtrosDeclarados, valoresTopo]);

    /** O nome da dimensão como a primeira fonte que a conhece a chama ("Responsável"). */
    const nomeDimensao = useCallback((dimensao: string) => {
        for (const bloco of painel?.blocos ?? []) {
            const achada = catalogo?.fontes
                .find(f => f.id === bloco.consulta.fonte)?.dimensoes
                .find(d => d.id === dimensao);
            if (achada) return achada.nome;
        }
        return dimensao;
    }, [painel, catalogo]);

    /* ─── o período que vale agora ───────────────────────────────── */

    const periodo = useMemo(() => {
        if (!painel) return null;
        if (editando || !lente || lente === painel.periodo.preset) return painel.periodo;
        // Trocar o preset zera o intervalo manual: um "de/até" de outro período mentiria.
        return { ...painel.periodo, preset: lente, de: null, ate: null };
    }, [painel, lente, editando]);

    /* ─── um resultado por bloco ─────────────────────────────────── */

    /* Em edição o filtro do topo não entra: ali a pessoa está mexendo no relatório, e um
       recorte invisível na consulta faria o bloco mostrar um número que o bloco não pede. */
    const filtrosDoTopo = useMemo<FiltroEspecificacao[]>(() => (
        editando ? [] : filtrosDeclarados
            .filter(d => filtroTopo[d])
            .map(d => ({ dimensao: d, operador: 'in', valores: [filtroTopo[d]] }))
    ), [editando, filtrosDeclarados, filtroTopo]);

    useEffect(() => {
        if (!painel || !periodo) return;

        painel.blocos.forEach(bloco => {
            const dimensoesDaFonte = catalogo?.fontes
                .find(f => f.id === bloco.consulta.fonte)?.dimensoes.map(d => d.id) ?? [];
            const spec = consultaDoBloco(bloco, periodo, filtrosDoTopo, dimensoesDaFonte);
            const chave = JSON.stringify(spec);
            if (assinaturas.current[bloco.id] === chave) return;
            assinaturas.current[bloco.id] = chave;

            setEstados(e => ({ ...e, [bloco.id]: { ...e[bloco.id], carregando: true } }));
            RelatorioPersonalizadoService.consultar(spec)
                .then(resultado => {
                    if (montado.current) setEstados(e => ({ ...e, [bloco.id]: { resultado, carregando: false } }));
                })
                .catch(err => {
                    if (montado.current) setEstados(e => ({
                        ...e,
                        [bloco.id]: { erro: err instanceof Error ? err.message : 'Falhou.', carregando: false },
                    }));
                });
        });

        // Bloco removido não deixa resultado pendurado — nem assinatura, para o
        // dia em que o mesmo id voltar (desfazer, duplicar) e precisar consultar.
        const vivos = new Set(painel.blocos.map(b => b.id));
        Object.keys(assinaturas.current).forEach(k => { if (!vivos.has(k)) delete assinaturas.current[k]; });
    }, [painel, periodo, filtrosDoTopo, catalogo]);

    /* ─── edição ─────────────────────────────────────────────────── */

    const mudarBloco = useCallback((novo: BlocoPainel) => {
        setPainel(p => (p ? { ...p, blocos: p.blocos.map(b => (b.id === novo.id ? novo : b)) } : p));
    }, []);

    const arranjar = useCallback((blocos: BlocoPainel[]) => {
        setPainel(p => (p ? { ...p, blocos } : p));
    }, []);

    const adicionar = () => {
        if (!painel || !catalogo) return;
        if (painel.blocos.length >= catalogo.limites.maximoBlocos) {
            aviso.atencao(`O limite é de ${catalogo.limites.maximoBlocos} blocos por relatório.`);
            return;
        }
        // Id de tempo: único dentro do painel e estável enquanto a tela viver.
        const idNovo = `b${Date.now().toString(36)}`;
        const vaga = proximaVaga(painel.blocos);
        const bloco = blocoNovo(catalogo, idNovo, vaga.x, vaga.y);
        setPainel({ ...painel, blocos: acomodar([...painel.blocos, bloco], idNovo) });
        setSelecionado(idNovo);
    };

    const remover = (idBloco: string) => {
        if (!painel) return;
        if (painel.blocos.length === 1) {
            aviso.atencao('Um relatório precisa de pelo menos um bloco.');
            return;
        }
        setPainel({ ...painel, blocos: painel.blocos.filter(b => b.id !== idBloco) });
        if (selecionado === idBloco) setSelecionado(null);
    };

    /* Cancelar volta ao que está GRAVADO, e não ao que a tela tinha antes do
       último gesto: quem cancela quer o relatório de volta como era, inteiro. */
    const cancelar = () => {
        if (criando) { navegar('/relatorios/novo'); return; }
        if (salvo.current) {
            setPainel(salvo.current.painel);
            setNome(salvo.current.nome);
            setVisibilidade(salvo.current.visibilidade);
            setLente(salvo.current.painel.periodo.preset);
        }
        setSelecionado(null);
        setEditando(false);
        navegar(`/relatorios/${id}`);
    };

    const salvar = async () => {
        if (!painel) return;
        if (!nome.trim()) { aviso.atencao('Dê um nome ao relatório antes de salvar.'); return; }

        setSalvando(true);
        try {
            const carga = { nome: nome.trim(), descricao: relatorio?.descricao ?? null, visibilidade, painel };
            if (criando) {
                const novo = await RelatorioPersonalizadoService.criar(carga);
                aviso.ok('Relatório salvo.');
                /* Sair do modo de edição é explícito: a rota muda de /novo/:modelo
                   para /relatorios/:id, mas o componente é o MESMO e nenhum estado
                   se recalcula sozinho — sem isto o painel recém-criado abria com a
                   barra de edição no lugar do "Editar painel". */
                salvo.current = { painel, nome: nome.trim(), visibilidade };
                setEditando(false);
                setSelecionado(null);
                navegar(`/relatorios/${novo.relatorioSalvoId}`);
            } else {
                await RelatorioPersonalizadoService.atualizar(Number(id), carga);
                aviso.ok('Relatório atualizado.');
                salvo.current = { painel, nome: nome.trim(), visibilidade };
                setEditando(false);
                setSelecionado(null);
                navegar(`/relatorios/${id}`);
            }
        } catch (e) {
            aviso.erro(textoDoErro(e, 'Não foi possível salvar.'));
        } finally {
            setSalvando(false);
        }
    };

    /* ─── render ─────────────────────────────────────────────────── */

    const voltar = (
        <button type="button" className="rl-back" aria-label="Voltar para Relatórios"
            onClick={() => navegar(criando ? '/relatorios/novo' : '/relatorios')}>
            <LuArrowLeft size={16} />
        </button>
    );

    if (carregando) {
        return (
            <div className="page-shell rl-screen">
                <PageHeader title="Relatório" subtitle="Carregando…" />
                <div className="page-body"><div className="rl-skeleton-bloco" /></div>
            </div>
        );
    }

    if (!painel || erro) {
        return (
            <div className="page-shell rl-screen">
                <PageHeader leading={voltar} title={nome || 'Relatório'} />
                <div className="page-body">
                    <div className="rl-vazio">
                        <p className="rl-vazio-t">Não foi possível mostrar este relatório</p>
                        <p className="rl-vazio-s">{erro}</p>
                    </div>
                </div>
            </div>
        );
    }

    const blocoSelecionado = painel.blocos.find(b => b.id === selecionado) ?? null;
    const podeEditar = criando || !!relatorio?.podeEditar;

    /* As medidas usadas no painel INTEIRO, sem repetir: um painel de sete blocos
       tem "faturamento" em três deles, e explicar o mesmo cálculo três vezes é
       ruído. A definição vem do catálogo, que é onde ela é escrita uma vez só. */
    const definicoes = catalogo
        ? [...new Map(
            painel.blocos.flatMap(b => {
                const fonte = catalogo.fontes.find(f => f.id === b.consulta.fonte);
                return b.consulta.medidas
                    .map(m => fonte?.medidas.find(x => x.id === m.id))
                    .filter((m): m is NonNullable<typeof m> => !!m);
            }).map(m => [m.id, m]),
        ).values()]
        : [];

    return (
        <div className="page-shell rl-screen">
            <PageHeader
                leading={voltar}
                title={nome || (criando ? 'Novo relatório' : 'Relatório')}
                titleInfo={!editando && definicoes.length > 0 ? (
                    <InfoTitulo rotulo="Como este relatório é calculado">
                        <span className="rl-info-t">Como este relatório é calculado</span>
                        {definicoes.map(m => (
                            <span key={m.id} className="rl-info-d">
                                <b>{m.nome}</b>
                                {m.definicao ?? 'Sem definição cadastrada.'}
                            </span>
                        ))}
                        <span className="rl-info-d">
                            <b>Quem vê o quê</b>
                            O alcance é o do seu perfil, não o do relatório. Administrador vê a
                            empresa inteira; os demais perfis veem apenas a própria carteira.
                        </span>
                    </InfoTitulo>
                ) : undefined}
                subtitle={editando
                    ? 'Editando · arraste pela barra do bloco, puxe o canto para redimensionar'
                    : relatorio?.descricao ?? undefined}
                actions={
                    <div className="rl-acoes">
                        {catalogo && (
                            <Select
                                size="control"
                                block={false}
                                icon={<LuCalendar size={14} />}
                                value={(editando ? painel.periodo.preset : lente ?? painel.periodo.preset)}
                                options={catalogo.presets
                                    .filter(p => p.id !== 'personalizado' || painel.periodo.preset === 'personalizado')
                                    .map(p => ({ value: p.id, label: p.nome }))}
                                onChange={v => (editando
                                    ? setPainel({ ...painel, periodo: { ...painel.periodo, preset: v, de: null, ate: null } })
                                    : setLente(v))}
                                ariaLabel="Período"
                            />
                        )}

                        {!editando && filtrosDeclarados.map(dimensao => (
                            <Select
                                key={dimensao}
                                size="control"
                                block={false}
                                icon={<LuUser size={14} />}
                                value={filtroTopo[dimensao] ?? ''}
                                options={[
                                    { value: '', label: `${nomeDimensao(dimensao)}: todos` },
                                    ...(valoresTopo[dimensao] ?? []).map(v => ({ value: v.id, label: v.nome })),
                                ]}
                                onChange={v => setFiltroTopo(f => ({ ...f, [dimensao]: v }))}
                                ariaLabel={nomeDimensao(dimensao)}
                            />
                        ))}

                        {!editando && podeEditar && (
                            <button type="button" className="rl-btn" onClick={() => { setEditando(true); setLente(null); }}>
                                <LuPencil size={14} /> Editar painel
                            </button>
                        )}
                    </div>
                }
            />

            <div className="page-body rl-fill">
                {editando && (
                    <div className="rl-ferramentas">
                        <input
                            className="rl-ferramentas-nome"
                            value={nome}
                            placeholder="Nome do relatório"
                            aria-label="Nome do relatório"
                            onChange={e => setNome(e.target.value)}
                        />

                        <Select
                            size="control"
                            block={false}
                            value={visibilidade}
                            options={[
                                { value: 'pessoal', label: 'Só eu' },
                                { value: 'empresa', label: 'A empresa toda' },
                            ]}
                            onChange={v => setVisibilidade(v as 'pessoal' | 'empresa')}
                            ariaLabel="Quem vê este relatório"
                        />

                        <span className="rl-ferramentas-c">
                            {painel.blocos.length} de {catalogo?.limites.maximoBlocos ?? 12} blocos
                        </span>

                        <button type="button" className="rl-btn" onClick={adicionar}>
                            <LuPlus size={14} /> Adicionar bloco
                        </button>
                        <button type="button" className="rl-btn" onClick={cancelar}>Cancelar</button>
                        <button type="button" className="rl-btn is-primary" disabled={salvando} onClick={() => void salvar()}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </button>
                    </div>
                )}

                <div className="rl-painel">
                    <div className="rl-painel-grade">
                        <PainelGrade
                            blocos={painel.blocos}
                            editando={editando}
                            selecionado={selecionado}
                            onSelecionar={setSelecionado}
                            onArranjar={arranjar}
                            onRemover={remover}
                            podeExportar={idBloco => (estados[idBloco]?.resultado?.linhas.length ?? 0) > 0}
                            onExportar={idBloco => {
                                const bloco = painel.blocos.find(x => x.id === idBloco);
                                const res = estados[idBloco]?.resultado;
                                if (!bloco || !res) return;
                                /* O nome junta painel e bloco: baixados três blocos do
                                   mesmo painel, "visao_de_vendas.csv" três vezes não
                                   diria qual é qual na pasta de Downloads. */
                                exportarResultadoCsv(res, `${nome} ${bloco.titulo ?? ''}`.trim());
                            }}
                            render={bloco => {
                                const e = estados[bloco.id];
                                if (e?.erro) {
                                    return (
                                        <div className="rl-bloco-erro">
                                            <LuTriangleAlert size={14} />
                                            <span>{e.erro}</span>
                                        </div>
                                    );
                                }
                                if (!e?.resultado) return <div className="rl-skeleton-bloco" />;
                                if (e.resultado.linhas.length === 0) {
                                    return <div className="rl-bloco-vazio">Nada no período.</div>;
                                }
                                /* O piso do gráfico acompanha o bloco: um bloco de
                                   3 linhas tem ~254px de corpo, e o piso de página
                                   inteira (240) transbordava. */
                                return (
                                    <RelatorioResultado
                                        resultado={e.resultado}
                                        visual={bloco.consulta.visual}
                                        alturaMinima={Math.max(80, bloco.h * 88 - 70)}
                                    />
                                );
                            }}
                            /* Os avisos do servidor ficam NO BLOCO que os gerou, e não
                               numa faixa no topo da página: num painel de sete blocos,
                               "negócios com mais de um motivo contam pelo primeiro" lá
                               em cima não diz de qual número ele está falando. */
                            rodape={bloco => {
                                const res = estados[bloco.id]?.resultado;
                                if (!res || res.avisos.length === 0) return null;
                                return (
                                    <div className="rl-bloco-nota">
                                        {res.avisos.map(a => <span key={a}>{a}</span>)}
                                    </div>
                                );
                            }}
                        />
                    </div>

                    {editando && blocoSelecionado && catalogo && (
                        <BlocoEditor
                            bloco={blocoSelecionado}
                            catalogo={catalogo}
                            resultado={estados[blocoSelecionado.id]?.resultado ?? null}
                            onMudar={mudarBloco}
                            onFechar={() => setSelecionado(null)}
                            periodoEmPalavras={nomePreset(catalogo, painel.periodo.preset)}
                        />
                    )}
                </div>

            </div>
        </div>
    );
}
